import { getAuthHeaders } from '@/hooks/useAuth';

// Embed mode: when the panel runs inside casting's Game attendee page
// (cross-origin iframe), the parent hands us the shared-auth token via
// postMessage and we use it instead of localStorage (which may be
// partitioned inside third-party iframes).
let embedToken: string | null = null;
export function setEmbedToken(t: string) { embedToken = t; }
function authHeaders(): Record<string, string> {
  if (embedToken) return { 'Content-Type': 'application/json', Authorization: `Bearer ${embedToken}` };
  return getAuthHeaders();
}

// ── Game Show API client ──────────────────────────────────────────────
// Base: /api/gameshow. Auth via getAuthHeaders(). The backend is being built
// in parallel — these types + calls match that contract byte-for-byte.

const BASE = '/api/gameshow';

export type ShowFormat = 'quiz' | 'battle';
export type ShowStatus = 'lobby' | 'live' | 'paused' | 'ended';
export type Delivery = 'host_read' | 'on_screen' | 'both';
export type QuestionState = 'queued' | 'open' | 'locked' | 'revealed';
export type LiveQuestionState = 'open' | 'locked' | 'revealed';
// Stage gate (Come On Down): strict top-scorers, or a random draw from the top N.
export type GateMode = 'strict' | 'top_n_draw';

export interface ShowSettings {
  gateMode?: GateMode;
  topN?: number;
  defaultDecaySeconds?: number;
  lockSeconds?: number;
  bracketFormat?: string;
}

export interface Show {
  _id: string;
  title: string;
  format: ShowFormat;
  status: ShowStatus;
  settings?: ShowSettings;
  gameNumber?: number;
  createdAt?: string;
}

// A saved run in the show's game history (newest first from GET /runs).
export interface GameRun {
  gameNumber: number;
  endedAt: string;
  playerCount: number;
  winner: { userId: string; userName: string; totalPoints: number } | null;
}

export interface HostQuestion {
  _id: string;
  order: number;
  text: string;
  options: string[];
  correctOption: number | null; // null = vote question
  points: number;
  decaySeconds: number;
  delivery: Delivery;
  state: QuestionState;
  isGate: boolean;
  answerCount: number;
}

export interface LeaderboardRow {
  userName: string;
  totalPoints: number;
  rank: number;
}

// A drawn contestant with their standing at draw time (from POST /draw).
export interface DrawnContestant {
  userId: string;
  userName: string;
  rank: number;
  totalPoints: number;
}

// Host-facing contestant row (rank is null if they have no score yet).
export interface HostContestant {
  userId: string;
  userName: string;
  drawnAt?: string;
  status: string;
  rank: number | null;
  totalPoints: number;
}

export interface DrawResponse {
  drawn: DrawnContestant[];
  mode: GateMode;
}

export interface OptInResponse {
  optedIn: boolean;
  optInCount: number;
}

// ── Sponsorship layer ──
// Prizes are sponsor-funded. The host free-texts what a sponsor gets + a price;
// we mint a shareable pay link; the sponsor pays via Stripe; the paid sponsor's
// name is shown on-air as prize provenance.
export type SponsorshipStatus = 'pending' | 'paid' | 'void';

export interface Sponsorship {
  _id: string;
  showId: string;
  offerText: string;
  amountCents: number;
  status: SponsorshipStatus;
  sponsorName: string | null;
  payLinkToken: string;
  payUrl: string; // https://post.heyjoe.io/sponsor/<token>
  paidAt?: string | null;
  createdAt?: string;
}

// Sponsor-facing public view of an offer (GET /sponsor/:token).
export interface PublicSponsorship {
  showTitle: string;
  hostName: string;
  offerText: string;
  amountCents: number;
  status: SponsorshipStatus;
}

// ── Video Battle ──
// Matches are the data model; the bracket is just a generator. A performer
// uploads one self-tape (free); the host casts tapes into head-to-head matches;
// the audience votes; the host reveals.
export type TapeStatus = 'submitted' | 'cast' | 'rejected';
export type MatchState = 'queued' | 'open' | 'locked' | 'revealed';
export type LiveMatchState = 'open' | 'locked' | 'revealed';
export type MatchSide = 'a' | 'b';

export interface Tape {
  _id: string;
  showId: string;
  userId: string;
  userName: string;
  title: string;
  s3Key: string;
  status: TapeStatus;
  submittedAt?: string;
  videoUrl?: string;
}

// Compact tape shape embedded in host/state match rows.
export interface MatchTape {
  title: string;
  userName: string;
  videoUrl: string;
}

// Host-facing match row (with the resolved tapes + live vote counts).
export interface HostMatch {
  _id: string;
  order: number;
  round: number;
  state: MatchState;
  tapeA: (MatchTape & { _id: string }) | null;
  tapeB: (MatchTape & { _id: string }) | null;
  votes: { a: number; b: number };
  openedAt?: string | null;
  lockedAt?: string | null;
}

// The audience-facing current match (mirrors StateCurrentQuestion).
export interface StateCurrentMatch {
  id: string;
  state: LiveMatchState;
  round: number;
  tapeA: MatchTape | null;
  tapeB: MatchTape | null;
  myVote: MatchSide | null;
  tally?: { a: number; b: number }; // locked/revealed only
  openedAt: number | string;
  serverNow: number | string;
}

export interface HostResponse {
  show: Show;
  questions: HostQuestion[];
  leaderboard: LeaderboardRow[]; // top 20
  optInCount: number;
  contestants: HostContestant[];
  sponsorships: Sponsorship[];
  // Battle shows only — empty arrays for quiz shows.
  tapes: Tape[];
  matches: HostMatch[];
}

// The shape sent up to PUT /questions — replaces the queued set.
export interface QuestionDraft {
  text: string;
  options: string[];
  correctOption: number | null;
  points?: number;
  decaySeconds?: number;
  delivery?: Delivery;
  isGate?: boolean;
}

export interface StateCurrentQuestion {
  id: string;
  state: LiveQuestionState;
  delivery: Delivery;
  options: string[];
  text?: string; // ABSENT for host-read questions
  points: number;
  decaySeconds: number;
  lockSeconds: number;
  openedAt: number | string; // ms epoch (or ISO) — normalize with new Date()
  serverNow: number; // ms epoch on the server clock
  myAnswer: number | null;
  tally?: number[];
  correctOption?: number;
}

interface BaseStateResponse {
  status: ShowStatus;
  format: ShowFormat;
  title: string;
  currentQuestion: null | StateCurrentQuestion;
  // Battle shows: the live/last matchup (null for quiz shows or idle battles).
  currentMatch: null | StateCurrentMatch;
  leaderboard: {
    top: LeaderboardRow[];
    me: { rank: number; totalPoints: number } | null;
  };
  answeredCount: number;
  // Prize provenance: the newest PAID sponsor, or null. Shown on-air.
  sponsor: { name: string } | null;
}

export interface StateResponse extends BaseStateResponse {
  // Come On Down: stage-pool signals for the audience page.
  optInCount: number;
  myOptIn: boolean;
  myContestant: boolean;
}

// GET /contestant-state — same core shape, gated to drawn contestants; text
// is always present (contestants are on camera).
export interface ContestantStateResponse extends BaseStateResponse {
  isContestant?: boolean;
}

export interface AnswerResult {
  accepted: boolean;
  latencyMs: number;
  pointsEarned?: number;
}

// Error that carries the HTTP status so callers can detect a 409 conflict.
// `code` and `body` surface machine-readable failure details (e.g. the
// REGISTRATION_REQUIRED gate carries the productId to link to).
export class GameShowApiError extends Error {
  status: number;
  code?: string;
  body?: Record<string, unknown>;
  constructor(status: number, message: string, body?: Record<string, unknown>) {
    super(message);
    this.name = 'GameShowApiError';
    this.status = status;
    this.body = body;
    this.code = typeof body?.code === 'string' ? (body.code as string) : undefined;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await res.json();
      if (body?.error || body?.message) msg = (body.error || body.message) as string;
    } catch {
      /* non-JSON error body */
    }
    throw new GameShowApiError(res.status, msg, body);
  }
  // Some POSTs may return empty bodies.
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export const gameshowApi = {
  // NOTE: the backend wraps single shows as { show } and lists as { shows } —
  // unwrap here (defensively accepting both shapes) so callers use plain types.
  createShow: (title: string, format: ShowFormat, settings?: Record<string, unknown>) =>
    req<{ show?: Show } & Show>('/shows', {
      method: 'POST',
      body: JSON.stringify({ title, format, settings }),
    }).then((r) => (r.show ?? r) as Show),

  myShows: () =>
    req<{ shows?: Show[] } | Show[]>('/shows/mine').then((r) =>
      (Array.isArray(r) ? r : r.shows ?? []) as Show[]
    ),

  // Newest show linked to a product (host-auth) — used by the featured event builder
  // in gameshow mode to resolve the linked show on edit.
  forProduct: (productId: string) => req<{ show: Show }>(`/shows/for-product/${productId}`),

  host: (showId: string) => req<HostResponse>(`/shows/${showId}/host`),

  saveQuestions: (showId: string, questions: QuestionDraft[]) =>
    req<HostResponse>(`/shows/${showId}/questions`, {
      method: 'PUT',
      body: JSON.stringify({ questions }),
    }),

  openQuestion: (showId: string, qid: string) =>
    req<unknown>(`/shows/${showId}/questions/${qid}/open`, { method: 'POST' }),

  lockQuestion: (showId: string, qid: string) =>
    req<unknown>(`/shows/${showId}/questions/${qid}/lock`, { method: 'POST' }),

  revealQuestion: (showId: string, qid: string) =>
    req<unknown>(`/shows/${showId}/questions/${qid}/reveal`, { method: 'POST' }),

  setStatus: (showId: string, status: ShowStatus) =>
    req<unknown>(`/shows/${showId}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  // ── Run lifecycle ──
  // Host: save the current game's scores to history, then wipe the board and
  // return the show at status 'lobby' with an incremented gameNumber.
  newGame: (showId: string) => req<{ show: Show }>(`/shows/${showId}/new-game`, { method: 'POST' }),

  // Host: list saved runs (newest first).
  runs: (showId: string) => req<{ runs: GameRun[] }>(`/shows/${showId}/runs`),

  // Host: download scores as CSV. Omit `game` for the current live leaderboard;
  // pass a gameNumber for a saved run. Triggers a browser download.
  exportScores: async (showId: string, game?: number) => {
    const url = `${BASE}/shows/${showId}/export${game != null ? `?game=${game}` : ''}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) throw new GameShowApiError(res.status, 'Export failed');
    const blob = await res.blob();
    // Parse filename from Content-Disposition (fallback scores.csv).
    let filename = 'scores.csv';
    const cd = res.headers.get('Content-Disposition');
    if (cd) {
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      if (match?.[1]) filename = decodeURIComponent(match[1]);
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  },

  state: (showId: string) => req<StateResponse>(`/shows/${showId}/state`),

  answer: (showId: string, questionId: string, optionIdx: number) =>
    req<AnswerResult>(`/shows/${showId}/answer`, {
      method: 'POST',
      body: JSON.stringify({ questionId, optionIdx }),
    }),

  // ── Come On Down ──
  // Audience: opt in / out of the stage pool.
  optIn: (showId: string) => req<OptInResponse>(`/shows/${showId}/optin`, { method: 'POST' }),
  optOut: (showId: string) => req<OptInResponse>(`/shows/${showId}/optin`, { method: 'DELETE' }),

  // Host: draw `count` contestants from the opted-in pool (per the show's gateMode).
  draw: (showId: string, count: number) =>
    req<DrawResponse>(`/shows/${showId}/draw`, { method: 'POST', body: JSON.stringify({ count }) }),

  // Host: list drawn contestants, and eject one.
  contestants: (showId: string) => req<{ contestants: HostContestant[] }>(`/shows/${showId}/contestants`),
  ejectContestant: (showId: string, userId: string) =>
    req<{ ok: boolean }>(`/shows/${showId}/contestants/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  // Host: update the show's settings (gateMode / topN / decay / lock / bracket).
  updateSettings: (showId: string, settings: Partial<ShowSettings>) =>
    req<{ settings: ShowSettings }>(`/shows/${showId}/settings`, {
      method: 'PATCH',
      body: JSON.stringify({ settings }),
    }),

  // Contestant: on-stage desktop state (question text always present).
  contestantState: (showId: string) => req<ContestantStateResponse>(`/shows/${showId}/contestant-state`),

  // ── Sponsorship ──
  // Host: create a sponsorship (pending) + get its shareable pay link. Backend
  // wraps as { sponsorship } — unwrap (defensively accept both shapes).
  createSponsorship: (showId: string, offerText: string, amountCents: number) =>
    req<{ sponsorship?: Sponsorship } & Sponsorship>(`/shows/${showId}/sponsorships`, {
      method: 'POST',
      body: JSON.stringify({ offerText, amountCents }),
    }).then((r) => (r.sponsorship ?? r) as Sponsorship),

  // Host: list sponsorships (all statuses).
  sponsorships: (showId: string) =>
    req<{ sponsorships?: Sponsorship[] } | Sponsorship[]>(`/shows/${showId}/sponsorships`).then((r) =>
      (Array.isArray(r) ? r : r.sponsorships ?? []) as Sponsorship[]
    ),

  // Host: void a pending sponsorship.
  voidSponsorship: (sponsorshipId: string) =>
    req<{ ok: boolean }>(`/sponsorships/${sponsorshipId}`, { method: 'DELETE' }),

  // Public: fetch the sponsor-facing offer (no auth required server-side).
  sponsorOffer: (token: string) => req<PublicSponsorship>(`/sponsor/${token}`),

  // Public: start the sponsor Stripe Checkout → { url } to redirect to.
  sponsorCheckout: (token: string, sponsorName: string, sponsorEmail: string) =>
    req<{ url: string }>(`/sponsor/${token}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ sponsorName, sponsorEmail }),
    }),

  // ── Video Battle ──
  // Performer: presigned PUT for a self-tape (upload the file, then register it).
  tapeUploadUrl: (showId: string, fileName: string, fileType: string) =>
    req<{ uploadUrl: string; key: string }>(`/shows/${showId}/tapes/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ fileName, fileType }),
    }),

  // Performer: register (or replace) my tape. Backend wraps as { tape }.
  submitTape: (showId: string, title: string, s3Key: string) =>
    req<{ tape?: Tape } & Tape>(`/shows/${showId}/tapes`, {
      method: 'POST',
      body: JSON.stringify({ title, s3Key }),
    }).then((r) => (r.tape ?? r) as Tape),

  // Performer: my own tape for this show (null if not yet submitted).
  myTape: (showId: string) => req<{ tape: Tape | null }>(`/shows/${showId}/my-tape`).then((r) => r.tape),

  // Host: list all submitted tapes (with playable proxy URLs).
  tapes: (showId: string) =>
    req<{ tapes?: Tape[] } | Tape[]>(`/shows/${showId}/tapes`).then((r) =>
      (Array.isArray(r) ? r : r.tapes ?? []) as Tape[]
    ),

  // Host: cast the bracket from an ordered tape selection (per settings.bracketFormat).
  generateMatches: (showId: string, tapeIds: string[]) =>
    req<{ matches: HostMatch[] }>(`/shows/${showId}/matches/generate`, {
      method: 'POST',
      body: JSON.stringify({ tapeIds }),
    }).then((r) => r.matches),

  // Host: match transitions (mirror the question machine).
  openMatch: (showId: string, mid: string) =>
    req<unknown>(`/shows/${showId}/matches/${mid}/open`, { method: 'POST' }),
  lockMatch: (showId: string, mid: string) =>
    req<unknown>(`/shows/${showId}/matches/${mid}/lock`, { method: 'POST' }),
  revealMatch: (showId: string, mid: string) =>
    req<unknown>(`/shows/${showId}/matches/${mid}/reveal`, { method: 'POST' }),

  // Audience: vote for side 'a' or 'b' in the open matchup.
  voteMatch: (showId: string, mid: string, side: MatchSide) =>
    req<{ accepted: boolean }>(`/shows/${showId}/matches/${mid}/vote`, {
      method: 'POST',
      body: JSON.stringify({ side }),
    }),
};

// Speed-decay curve — cosmetic client estimate for the live points meter.
// The authoritative points come from POST /answer → pointsEarned. Shape
// mirrors the design comp: full points through a short grace window, linear
// decay to a ~20% floor by decaySeconds.
export function pointsNow(elapsedMs: number, points: number, decaySeconds: number): number {
  const t = Math.max(0, elapsedMs) / 1000;
  const grace = Math.min(2, decaySeconds * 0.2);
  const floor = points * 0.2;
  if (t <= grace) return points;
  if (t >= decaySeconds) return Math.round(floor);
  const frac = (t - grace) / (decaySeconds - grace);
  return Math.round(points - (points - floor) * frac);
}
